import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PublicNavbar } from './PublicNavbar';
import { Home as HomeIcon, Mail, Phone, MapPin, Linkedin, Twitter } from 'lucide-react';

export function PublicLayout({ children, className = '' }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicNavbar />
      <main className={`flex-1 ${className}`}>
        {children}
      </main>
      <PublicFooter />
    </div>
  );
}

// Footer link component that scrolls to top
function FooterLink({ to, children }) {
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    navigate(to);
  };
  
  return (
    <a 
      href={to} 
      onClick={handleClick}
      className="text-slate-300 hover:text-white text-sm transition-colors cursor-pointer"
    >
      {children}
    </a>
  );
}

function PublicFooter() {
  const footerLinks = {
    landlords: [
      { label: 'Overview', href: '/landlords' },
      { label: 'Listing & Marketing', href: '/landlords/listing' },
      { label: 'Compliance', href: '/landlords/compliance' },
      { label: 'Pricing', href: '/landlords/pricing' },
    ],
    tenants: [
      { label: 'Overview', href: '/tenants' },
      { label: 'Find & Apply', href: '/tenants/find-apply' },
      { label: 'Referencing', href: '/tenants/referencing' },
      { label: 'FAQs', href: '/tenants/faqs' },
    ],
    company: [
      { label: 'How It Works', href: '/how-it-works' },
      { label: 'Blog', href: '/blog' },
      { label: 'For Contractors', href: '/contractors' },
      { label: 'For Agents', href: '/agents' },
      { label: 'Contact', href: '/contact' },
    ],
    legal: [
      { label: 'Terms of Business', href: '/terms-of-business' },
      { label: 'Complaints Procedure', href: '/complaints' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Cookie Policy', href: '/cookies' },
    ],
  };

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="p-1.5 bg-primary rounded-lg">
                <HomeIcon className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-display font-bold">LetWise</span>
            </Link>
            <p className="text-slate-400 text-sm mb-4">
              Modern lettings management for landlords, tenants, and contractors.
            </p>
            <div className="flex gap-3">
              <a href="#" className="p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="#" className="p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Link Columns */}
          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-slate-400">Landlords</h4>
            <ul className="space-y-2">
              {footerLinks.landlords.map((link) => (
                <li key={link.href}>
                  <FooterLink to={link.href}>{link.label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-slate-400">Tenants</h4>
            <ul className="space-y-2">
              {footerLinks.tenants.map((link) => (
                <li key={link.href}>
                  <FooterLink to={link.href}>{link.label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-slate-400">Company</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <FooterLink to={link.href}>{link.label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-2 md:col-span-1">
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-slate-400">Legal</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <FooterLink to={link.href}>{link.label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Memberships & Accreditations */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-8">
            <div className="flex items-center gap-4 bg-white rounded-lg px-4 py-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_letwise-review/artifacts/le6yp68v_PRS_Logo_high.jpg" 
                alt="Property Redress Scheme" 
                className="h-12 w-auto object-contain"
              />
              <div className="text-slate-900 text-sm">
                <p className="font-semibold">Property Redress Scheme</p>
                <p className="text-slate-600">Member: <span className="font-mono font-medium">PRS056298</span></p>
              </div>
            </div>
          </div>
          
          {/* Company Details */}
          <div className="text-center mb-6">
            <p className="text-slate-400 text-sm">
              <strong className="text-slate-300">C&C LET MANAGEMENT LIMITED</strong> trading as <strong className="text-slate-300">LetWise</strong>
            </p>
            <p className="text-slate-500 text-xs mt-1">
              Company No. SC869470 | Unit 4, 416 Hamilton Road, Cambuslang, Glasgow, G72 7XR
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-sm text-center sm:text-left">
            © {new Date().getFullYear()} C&C LET MANAGEMENT LIMITED trading as LetWise. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-sm text-slate-400">
            <span className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4" /> Scotland, UK
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default PublicLayout;
